'use client';

import { SessionDropdownMenu } from '@app/(main)/(normal)/ai/conv-search/_components/SessionHistoryDropdown';
import { type SessionSummary } from 'conversational-search-client';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { type JSX, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Icon, Text, Button } from '@videoblocks/design-system';

import { AiDisclosure } from './chat/AiDisclosure';

const INSPIRATION_PROMPTS = [
    'Impacts of ecological disasters',
    'Aerial cityscape at golden hour',
    'Dramatic dark and scary scene',
];

// When audio is in scope we surface a music-based example instead of a
// footage-only one so the entry points reflect the full search experience.
const AUDIO_INSPIRATION_PROMPTS = [
    'Impacts of ecological disasters',
    'Aerial cityscape at golden hour',
    'Upbeat jazz music',
];

const FOOTAGE_PLACEHOLDER = "Describe the shot — scene, mood, context — and we'll find what fits...";
const AUDIO_PLACEHOLDER =
    "Describe the shot or track you're looking for — scene, mood, context — and we'll find what fits...";

export function ConvSearchHero({
    sessions,
    supportsAudio = false,
}: {
    sessions: SessionSummary[];
    supportsAudio?: boolean;
    supportsImages?: boolean;
}): JSX.Element {
    const inspirationPrompts = supportsAudio ? AUDIO_INSPIRATION_PROMPTS : INSPIRATION_PROMPTS;
    const placeholder = supportsAudio ? AUDIO_PLACEHOLDER : FOOTAGE_PLACEHOLDER;
    const submitLabel = supportsAudio ? 'Search' : 'Find Footage';
    const [query, setQuery] = useState('');
    const router = useRouter();
    const [isSessionMenuOpen, setIsSessionMenuOpen] = useState(false);
    const sessionMenuContainerRef = useRef<HTMLDivElement>(null);

    function handleSubmit(): void {
        const trimmed = query.trim();
        let url = '/ai/conv-search/new';
        if (trimmed) {
            url += `?prompt=${encodeURIComponent(trimmed)}`;
        }
        router.push(url);
    }

    return (
        <section className="flex flex-col items-center justify-center gap-12 bg-gray-900 px-4 pb-12 pt-18">
            {/* Text content */}
            <div className="flex flex-col items-center gap-6">
                <Text
                    fontFamily="polysans"
                    fontSize="sm"
                    fontWeight="semibold"
                    className="uppercase tracking-wide text-bubblegum-300"
                >
                    Conversational Search
                </Text>

                <h1 className="max-w-3xl text-center font-polysans">
                    <Text asChild fontFamily="polysans" fontSize="5xl" fontWeight="semibold">
                        <span className="text-white">
                            Describe what you want.
                            <br />
                            <span className="text-yellow-300">Storyblocks finds it for you.</span>
                        </span>
                    </Text>
                </h1>

                <Text
                    fontFamily="polysans"
                    fontSize="xl"
                    fontWeight="regular"
                    className="max-w-2xl text-center text-white"
                >
                    Find the right content faster with Conversational Search as your creative partner. Just say what
                    you’re looking for and AI-powered search explores our library for you to find the perfect assets.
                </Text>
            </div>

            {/* Search area */}
            <div className="flex flex-col items-center gap-6">
                {/* Search card */}
                <div className="w-full max-w-3xl">
                    <div className="rounded-2xl border-8 border-gray-800 bg-gray-100 p-1 shadow-[0px_0px_60px_0px_rgba(0,0,0,0.4)]">
                        {/* Textarea */}
                        <div className="w-full rounded-2xl border border-transparent bg-white px-6 py-6">
                            <textarea
                                name="search-query"
                                value={query}
                                onChange={(e): void => setQuery(e.target.value)}
                                onKeyDown={(e): void => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSubmit();
                                    }
                                }}
                                placeholder={placeholder}
                                className="h-30 w-full resize-y bg-transparent text-lg text-gray-900 outline-none placeholder:text-gray-600"
                                aria-label="Describe what you're looking for"
                            />
                        </div>

                        {/* Bottom bar */}
                        <div className="flex px-2 py-2">
                            <div ref={sessionMenuContainerRef} className="relative flex items-center">
                                <Icon
                                    name="circleClock"
                                    variant="black"
                                    width={20}
                                    height={20}
                                    className={twMerge(!sessions.length && 'opacity-50')}
                                />
                                <button
                                    type="button"
                                    disabled={!sessions.length}
                                    className={twMerge(
                                        'flex items-center gap-1 px-2 text-sm font-semibold text-gray-700',
                                        !sessions.length && 'cursor-not-allowed opacity-50',
                                    )}
                                    onClick={() => setIsSessionMenuOpen((open) => !open)}
                                >
                                    Continue a search
                                </button>
                                <Icon
                                    name="chevronDown"
                                    width={15}
                                    height={15}
                                    className={twMerge('text-gray-700', !sessions.length && 'opacity-50')}
                                />
                                <SessionDropdownMenu
                                    isOpen={isSessionMenuOpen}
                                    setIsOpen={setIsSessionMenuOpen}
                                    sessions={sessions}
                                    containerRef={sessionMenuContainerRef}
                                />
                            </div>

                            <div className="ml-auto">
                                <Button onClick={handleSubmit}>{submitLabel}</Button>
                            </div>
                        </div>
                    </div>
                    <AiDisclosure />
                </div>

                {/* Inspiration prompts */}
                <div className="mt-2 flex flex-wrap justify-center gap-2">
                    {inspirationPrompts.map((prompt) => (
                        <Link
                            key={prompt}
                            href={`/ai/conv-search/new?prompt=${encodeURIComponent(prompt)}`}
                            className="flex items-center gap-4 rounded-full border border-gray-600 bg-gray-800/50 py-2 pl-4 pr-6 text-base font-semibold text-white transition-colors hover:bg-gray-700/50"
                        >
                            <Icon name="search" width={16} height={16} className="shrink-0 [&_path]:fill-white" />
                            {prompt}
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}
